import Appointments from "./appointments";
import Medications from "./medications";
import Consultations from "./consultations";
import { useQuery } from "@tanstack/react-query";
import { todayQuickActionsEncountersQueryOptions } from "../api/queries";
import { useParams } from "@tanstack/react-router";
import { getApiProgram } from "~/lib/utils";
import NoPreviewCard from "./cards/no-preview-card";
import { QuickActionCardSkeleton } from "./cards/quick-action-card-skeleton";
import { QUICK_ACTIONS_POLLING_INTERVAL } from "../types/constant";

const EncounterPreview = () => {
  const { program } = useParams({ strict: false });
  const apiProgram = getApiProgram(program ?? "");

  // Query all three types to check if there's any data
  const { data: consultationsData, isLoading: consultationsLoading } = useQuery(
    {
      ...todayQuickActionsEncountersQueryOptions({
        limit: 1,
        skip: 0,
        type: "consultation",
        program: apiProgram,
        type_sort_order: "consultation",
        status: "unresolved",
        sort: "new_old"
      }),
      refetchInterval: QUICK_ACTIONS_POLLING_INTERVAL
    }
  );

  const { data: appointmentsData, isLoading: appointmentsLoading } = useQuery(
    {
      ...todayQuickActionsEncountersQueryOptions({
      limit: 1,
      skip: 0,
      type: "appointment",
      program: apiProgram,
      type_sort_order: "appointment",
      status: "unresolved",
      sort: "new_old"
    }),
    refetchInterval: QUICK_ACTIONS_POLLING_INTERVAL
  }
  );

  const { data: medicationsData, isLoading: medicationsLoading } = useQuery(
    {
      ...todayQuickActionsEncountersQueryOptions({
      limit: 1,
      skip: 0,
      type: "refill",
      program: apiProgram,
      type_sort_order: "refill",
      status: "unresolved",
      sort: "new_old"
    }),
    refetchInterval: QUICK_ACTIONS_POLLING_INTERVAL
  }
  );


  const isLoading = consultationsLoading || appointmentsLoading || medicationsLoading;

  // Check if all three have no data
  const hasConsultations = consultationsData && consultationsData.totalCount && consultationsData.totalCount > 0;
  const hasAppointments = appointmentsData && appointmentsData.totalCount && appointmentsData.totalCount > 0;
  const hasMedications = medicationsData && medicationsData.totalCount && medicationsData.totalCount > 0;
  const hasAnyEncounters = hasConsultations || hasAppointments || hasMedications;

  return (
    <div aria-label="today-encounters" className="flex flex-col gap-[24px]">
      <h2 className="text-2xl font-medium">Latest Encounters</h2>

      {isLoading ? (
        <QuickActionCardSkeleton />
      ) : !hasAnyEncounters ? (
        <NoPreviewCard
          icon={program === "urgent-care" ? "/assets/monitor.png" : "/assets/notifications.svg"}
          title="Nothing to Display"
          description="You have no encounters to see today, when you do, they will appear here"
          className="w-full h-[440px]"
        />
      ) : (
        <div className="flex flex-col gap-6">
          {/* Consultations Section */}
          <div className="flex flex-col gap-4">
            <div className="flex text-sm font-normal border-b pb-2 text-[#202020CC]">
              Consultations {hasConsultations && (
                <div className="bg-[#FFAB1F] text-white px-1.5 py-0.5 rounded-full text-xs flex items-center ml-1.5">
                  {consultationsData.totalCount}
                </div>
              )}
            </div>
            {hasConsultations ? (
              <Consultations />
            ) : (
              <NoPreviewCard
                icon="/icons/prescription.svg"
                title="No Consultations"
                iconSize="w-10 h-10"
                description="You have no consultations to see today"
                className="w-full h-[150px]"
              />
            )}
          </div>

          {/* Medications Section */}
          <div className="flex flex-col gap-4">
            <div className="flex text-sm font-normal border-b pb-2 text-[#202020CC]">
              Medications Refill {hasMedications && (
                <div className="bg-[#FFAB1F] text-white px-1.5 py-0.5 rounded-full text-xs flex items-center ml-1.5">
                  {medicationsData.totalCount}
                </div>
              )}
            </div>
            {hasMedications ? (
              <Medications />
            ) : (
              <NoPreviewCard
                icon="/icons/refill.svg"
                title="No Medication Refills"
                iconSize="w-10 h-10"
                description="You have no medication refills to see today"
                className="w-full h-[150px]"
              />
            )}
          </div>

          {/* Appointments Section */}
          <div className="flex flex-col gap-4">
            <div className="flex text-sm font-normal border-b pb-2 text-[#202020CC]">
              Appointment {hasAppointments && (
                <div className="bg-[#FFAB1F] text-white px-1.5 py-0.5 rounded-full text-xs flex items-center ml-1.5">
                  {appointmentsData.totalCount}
                </div>
              )}
            </div>
            {hasAppointments ? (
              <Appointments />
            ) : (
              <NoPreviewCard
                icon="/icons/appointment.svg"
                title="No Appointments"
                iconSize="w-10 h-10"
                description="You have no appointments to see today"
                className="w-full h-[150px]"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EncounterPreview;